import { ChevronDown, Menu, X } from 'lucide-react';
import { useState } from 'react';
import logo from '../assets/pelagos.png';
import { TARGET_CHAIN_LABEL } from '../web3/config';
import { isDeferredAtLaunch } from '../web3/launchScope';
import { shortenAddress, useWallet } from '../web3/useWallet';

const ALL_VIEWS = [
  'Swap',
  'Pools',
  'Farm',
  'Earn',
  'AI Trading',
  'RWA',
  'Launchpad',
  'Staking',
  'Portfolio',
  'Governance',
  'Dashboard',
  'Lending',
  'Prediction Market',
  'Bridge',
  'Enterprise API',
] as const;

export type NavView = (typeof ALL_VIEWS)[number];

interface MenuItem {
  view: NavView;
  label?: string; // display override
  desc: string;
}

interface MenuGroup {
  label: string;
  view?: NavView; // direct link when no items
  items?: MenuItem[];
}

// exchange-style grouped navigation: a few top-level entries, details in
// hover dropdowns, flagship features (RWA / AI) promoted to top level
const MENU: MenuGroup[] = [
  { label: 'Overview', view: 'Dashboard' },
  {
    label: 'Trade',
    items: [
      { view: 'Swap', desc: '토큰 교환' },
      { view: 'Pools', desc: '유동성 공급 · 수수료 수익' },
      { view: 'Bridge', desc: '자산 이동 (락&민트 데모)' },
      { view: 'Prediction Market', label: 'Prediction', desc: '가격 예측 베팅' },
    ],
  },
  {
    label: 'Earn',
    items: [
      { view: 'Farm', desc: 'LP 토큰 파밍' },
      { view: 'Earn', label: 'Simple Earn', desc: '단일 자산 예치' },
      { view: 'Staking', desc: '락업 스테이킹 · 고정 APR' },
      { view: 'Lending', desc: '담보 대출' },
    ],
  },
  { label: 'RWA', view: 'RWA' },
  { label: 'Signals', view: 'AI Trading' },
  { label: 'Portfolio', view: 'Portfolio' },
  {
    label: 'More',
    items: [
      { view: 'Launchpad', desc: '신규 프로젝트 공개 세일' },
      { view: 'Governance', desc: 'DAO 투표 · 제안' },
      { view: 'Enterprise API', desc: '기업용 인프라 연동' },
    ],
  },
];

export default function Header({
  active,
  onNavigate,
}: {
  active: NavView;
  onNavigate: (view: NavView) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const goto = (view: NavView) => {
    onNavigate(view);
    setMobileOpen(false);
    setOpenGroup(null);
  };
  const {
    address,
    isConnected,
    isPending,
    isSwitching,
    wrongNetwork,
    switchError,
    balanceLabel,
    error,
    connectWallet,
    disconnect,
    switchToTargetChain,
  } = useWallet();

  return (
    <header className="flex items-center justify-between h-20 md:h-28 px-4 md:px-6 border-b border-abyss-700 bg-abyss-900/80 backdrop-blur relative z-30">
      <div className="flex items-center gap-2 md:gap-4">
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foam-200 hover:bg-abyss-700/60 transition-colors"
          aria-label="메뉴 열기"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <button
          onClick={() => onNavigate('Dashboard')}
          className="shrink-0 transition-transform hover:scale-105"
          aria-label="Pelagos DEX 홈"
        >
          <img src={logo} alt="Pelagos DEX" className="h-16 w-16 md:h-[100px] md:w-[100px] object-contain" />
        </button>

        <nav className="hidden md:flex items-center gap-0.5">
          {MENU.map((group) => {
            const isActive = group.view
              ? active === group.view
              : group.items?.some((item) => item.view === active) ?? false;

            if (group.view) {
              return (
                <button
                  key={group.label}
                  onClick={() => onNavigate(group.view!)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-tide-400 bg-tide-400/10'
                      : 'text-foam-300 hover:text-foam-100 hover:bg-abyss-700/60'
                  }`}
                >
                  {group.label}
                </button>
              );
            }

            const open = openGroup === group.label;
            return (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => setOpenGroup(group.label)}
                onMouseLeave={() => setOpenGroup(null)}
              >
                <button
                  onClick={() => setOpenGroup(open ? null : group.label)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                    isActive || open
                      ? 'text-tide-400 bg-tide-400/10'
                      : 'text-foam-300 hover:text-foam-100 hover:bg-abyss-700/60'
                  }`}
                >
                  {group.label}
                  <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="absolute left-0 top-full pt-1.5 w-60">
                    <div className="rounded-xl border border-abyss-700 bg-abyss-850 shadow-2xl shadow-black/40 overflow-hidden py-1.5">
                      {group.items!.map((item) => (
                        <button
                          key={item.view}
                          onClick={() => {
                            onNavigate(item.view);
                            setOpenGroup(null);
                          }}
                          className={`w-full text-left px-4 py-2.5 transition-colors ${
                            active === item.view ? 'bg-tide-400/10' : 'hover:bg-abyss-800'
                          }`}
                        >
                          <div
                            className={`text-sm font-medium flex items-center gap-1.5 ${
                              active === item.view ? 'text-tide-400' : 'text-foam-100'
                            }`}
                          >
                            {item.label ?? item.view}
                            {isDeferredAtLaunch(item.view) && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-abyss-700 text-foam-500 uppercase tracking-wide">
                                Soon
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-foam-500 mt-0.5">{item.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className="flex items-center gap-3">
        {isConnected && wrongNetwork && (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => switchToTargetChain()}
              disabled={isSwitching}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-coral-500/15 text-coral-400 hover:bg-coral-500/25 transition-colors disabled:opacity-60"
            >
              {isSwitching ? '전환 요청 중… 지갑을 확인하세요' : `Wrong network — Switch to ${TARGET_CHAIN_LABEL}`}
            </button>
            {switchError && !isSwitching && (
              <span className="text-[10px] leading-tight text-coral-400/80 max-w-[220px] text-right">
                자동 전환 실패 — 지갑 앱에서 {TARGET_CHAIN_LABEL} 네트워크를 직접 추가·선택해주세요
              </span>
            )}
          </div>
        )}

        {!wrongNetwork && (
          <div className="hidden lg:flex items-center gap-2 text-xs text-foam-500 px-3 py-1.5 rounded-md border border-abyss-700 bg-abyss-850">
            <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
            {TARGET_CHAIN_LABEL}
          </div>
        )}

        {isConnected && address ? (
          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-abyss-700 text-foam-100 hover:bg-abyss-600 transition-colors flex items-center gap-2"
            >
              {balanceLabel && <span className="text-foam-500 font-normal">{balanceLabel}</span>}
              {shortenAddress(address)}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-44 rounded-lg border border-abyss-700 bg-abyss-850 shadow-xl overflow-hidden z-20">
                <button
                  onClick={() => {
                    disconnect();
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2.5 text-sm text-coral-400 hover:bg-abyss-800 transition-colors"
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={connectWallet}
            disabled={isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap bg-tide-500 text-abyss-950 hover:bg-tide-400 transition-colors disabled:opacity-60"
          >
            {isPending ? 'Connecting...' : 'Connect Wallet'}
          </button>
        )}

        {error && (
          <div className="absolute top-full right-6 mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs max-w-xs">
            {error}
          </div>
        )}
      </div>

      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-20 bg-black/50 z-20"
            onClick={() => setMobileOpen(false)}
          />
          <nav className="md:hidden absolute top-full left-0 right-0 z-30 border-b border-abyss-700 bg-abyss-900 max-h-[calc(100vh-5rem)] overflow-y-auto py-2 shadow-2xl shadow-black/40">
            {MENU.map((group) => {
              if (group.view) {
                const isActive = active === group.view;
                return (
                  <button
                    key={group.label}
                    onClick={() => goto(group.view!)}
                    className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors ${
                      isActive ? 'text-tide-400 bg-tide-400/10' : 'text-foam-100 hover:bg-abyss-800'
                    }`}
                  >
                    {group.label}
                  </button>
                );
              }
              return (
                <div key={group.label} className="py-1">
                  <div className="px-5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-foam-600">
                    {group.label}
                  </div>
                  {group.items!.map((item) => (
                    <button
                      key={item.view}
                      onClick={() => goto(item.view)}
                      className={`w-full text-left px-5 py-2.5 transition-colors ${
                        active === item.view ? 'bg-tide-400/10' : 'hover:bg-abyss-800'
                      }`}
                    >
                      <div
                        className={`text-sm font-medium ${
                          active === item.view ? 'text-tide-400' : 'text-foam-100'
                        }`}
                      >
                        {item.label ?? item.view}
                      </div>
                      <div className="text-[11px] text-foam-500 mt-0.5">{item.desc}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </nav>
        </>
      )}
    </header>
  );
}
